import { ComponentType } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';

import { Location } from '@angular/common';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlagService } from '@vendasta/businesses';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';
import {
  ChipCell,
  ColumnDescriptor,
  MenuCell,
  MenuItem,
  MultilineCell,
  TableViewConfig,
  TableViewModel,
} from '@vendasta/uikit';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AccessRestrictedDialogComponent } from '../access';
import { HeaderButton, NavigationDepth } from '../common/header-button';
import { PartnerService } from '../core/partner.service';
import { VConfigService } from '../vconfig/vconfig.service';
import { ActionList } from './action-list';
import { ActionListsStoreService } from './action-lists.store.service';
import { ActionListDialog, ActionListDialogData, CreateListDialog, DeleteListDialog } from './actions';
import { Action } from './actions/action';

const ACTION_LIST_DIALOG_WIDTH = '660px';

enum ListType {
  Users = 'user-list',
}

@Component({
  templateUrl: './action-lists-overview.component.html',
  styleUrls: ['./action-lists-overview.component.scss'],
  standalone: false,
})
export class ActionListsOverviewComponent implements OnInit, OnDestroy {
  constructor(
    private activatedRoute: ActivatedRoute,
    private actionListsStoreService: ActionListsStoreService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private title: Title,
    private router: Router,
    private vconfigService: VConfigService,
    private partnerService: PartnerService,
    private feature: FeatureFlagService,
    private location: Location,
    protected readonly translateService: TranslateService,
  ) {}
  hasMore$: Observable<boolean>;
  loadingMore$: Observable<boolean>;
  actionLists$: Observable<ActionList[]>;
  toolbarText$: Observable<string>;
  search$$: Subject<string> = new Subject<string>();
  accountListImportUrl = '/bulk-import/';
  selectedTabIndex = 0;

  private subscriptions: Subscription[] = [];
  private availableActions: Action[];
  headerButtons: HeaderButton[];

  columns: ColumnDescriptor<ActionList>[] = [
    {
      title: 'Name',
      cellForItem: this.listNameCell.bind(this),
      alignment: 'left',
    },
    {
      title: '',
      cellForItem: this.inProgressCell.bind(this),
      alignment: 'left',
      config: {
        width: '200px', // roughly the width of the ACTION IN PROGRESS chip
      },
    },
    {
      title: 'Last Modified',
      cellForItem: (actionList) => moment(actionList.modified).format('MMM D, YYYY'),
      config: {
        width: '100px',
      },
      alignment: 'left',
    },
    {
      title: '',
      cellForItem: this.menuCell.bind(this),
      config: {
        width: '50px',
      },
      alignment: 'right',
    },
  ];

  actionListTable: TableViewModel<ActionList> = new TableViewModel<ActionList>({
    columns: this.columns,
    emptyText: 'No lists found',
    loadMoreItemsTriggered: this.loadMore.bind(this),
    fixedTable: true,
  } as TableViewConfig<ActionList>);

  private listNameCell(actionList: ActionList): MultilineCell {
    return new MultilineCell({
      title: actionList.name,
      subtitle: `${actionList.accounts} Account${actionList.accounts === 1 ? '' : 's'}`,
      link: {
        url: `/action-lists/${actionList.id}/accounts`,
      },
    });
  }

  private inProgressCell(actionList: ActionList): ChipCell {
    return new ChipCell({
      color: 'warn',
      text: actionList.inProgress ? 'ACTION IN PROGRESS' : '',
    });
  }

  private menuCell(actionList: ActionList): MenuCell {
    let menuItems: MenuItem[] = [];

    // if list has accounts, add actions to list
    if (actionList.accounts > 0 && this.availableActions) {
      menuItems = this.availableActions.map((action) => {
        return {
          text: action.name,
          click: () =>
            action.locked
              ? this.showAccessRestrictedDialog(action.featureId)
              : this.showActionDialog(actionList, action.dialog, action.data),
          disabled: actionList.inProgress || action.disabled,
          rightIcon: action.locked ? 'locked' : null,
          ...(action.disabled && {
            toolTip: this.translateService.instant(action?.toolTip ?? ''),
          }),
          // ...(action.disabled && {
          //   tooTip: action?.toolTip??'',
          // }),
        };
      });
    }

    // delete is always an option
    menuItems.push({
      text: 'Delete',
      click: () => this.onDeleteListPressed(actionList),
      disabled: actionList.inProgress,
    });

    return new MenuCell({
      menuItems: menuItems,
    });
  }

  ngOnInit(): void {
    this.headerButtons = [
      {
        title: 'Create List',
        callback: () => this.onCreateListPressed(),
        navigationDepth: NavigationDepth.secondary,
        buttonId: 'create-list',
      },
      {
        title: 'Import List',
        url: this.accountListImportUrl,
        buttonId: 'import-list',
      },
    ];
    this.actionLists$ = this.actionListsStoreService.actionLists$;
    this.hasMore$ = this.actionListsStoreService.hasMore$;
    this.loadingMore$ = this.actionListsStoreService.loadingMore$;
    this.subscriptions.push(
      this.search$$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          switchMap((search) => this.actionListsStoreService.updateSearch(search)),
        )
        .subscribe(),
    );

    this.toolbarText$ = combineLatest([
      this.actionListsStoreService.totalActionListsCount$,
      this.actionLists$,
      this.hasMore$,
      this.loadingMore$,
    ]).pipe(
      map(([totalCount, lists, hasMore, loading]: [number, ActionList[], boolean, boolean]) => {
        if (!hasMore && !loading) {
          return `Showing all ${totalCount} results`;
        }
        return `Showing ${(lists || []).length} of ${totalCount}`;
      }),
    );
    this.actionListsStoreService.initializeStore();
    this.subscriptions.push(
      this.actionListsStoreService
        .getAvailableActions()
        .pipe(
          tap((result) => {
            this.availableActions = result;
            this.actionListTable.updateColumnAtIndex(3, {
              cellForItem: this.menuCell.bind(this),
            });
          }),
        )
        .subscribe(),
    );
    this.subscriptions.push(
      this.actionListsStoreService.notification$.subscribe((notification) => {
        if (!notification) {
          return;
        }
        if (notification.error) {
          this.snackbarService.errorSnack(notification.text);
        } else {
          this.snackbarService.successSnack(notification.text);
        }
      }),
    );
    this.selectedTabIndex = this.activatedRoute.snapshot.params.listType === ListType.Users ? 1 : 0;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe);
    this.actionListsStoreService.destroyStore();
  }

  updateSearchTerm(searchTerm: string): void {
    this.search$$.next(searchTerm);
  }

  loadMore(): void {
    this.actionListsStoreService.loadMoreActionLists();
  }

  private showActionDialog(
    actionList: ActionList,
    dialog: ComponentType<ActionListDialog>,
    data: ActionListDialogData,
  ): void {
    const dialogRef = this.dialog.open(dialog, {
      width: ACTION_LIST_DIALOG_WIDTH,
      data: data,
    });
    dialogRef.componentInstance.actionList = actionList;
    dialogRef.afterClosed().subscribe((success) => {
      if (success) {
        this.router.navigateByUrl(`/action-lists/${actionList.id}/history`);
      }
    });
  }

  private showAccessRestrictedDialog(featureId: string): void {
    this.dialog.closeAll();
    this.dialog.open(AccessRestrictedDialogComponent, {
      width: ACTION_LIST_DIALOG_WIDTH,
      data: { featureId: featureId },
    });
  }

  onCreateListPressed(): void {
    const dialogRef = this.dialog.open(CreateListDialog, {
      width: ACTION_LIST_DIALOG_WIDTH,
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((listName) => {
        if (listName) {
          this.actionListsStoreService.createActionList(listName);
        }
      }),
    );
  }

  private onDeleteListPressed(actionList: ActionList): void {
    const deleteDialogRef = this.dialog.open(DeleteListDialog, {
      width: ACTION_LIST_DIALOG_WIDTH,
    });
    deleteDialogRef.componentInstance.actionList = actionList;
    this.subscriptions.push(
      deleteDialogRef.afterClosed().subscribe((deleteFlag) => {
        if (deleteFlag) {
          this.actionListsStoreService.deleteActionList(actionList);
        }
      }),
    );
  }

  navigateToTabIndex(e: MatTabChangeEvent): void {
    const route = e.index === 0 ? 'action-lists/manage' : 'action-lists/manage/user-list';
    let query = '';
    if (this.location.path().indexOf('?') > 0) {
      query = this.location.path().split('?')[1];
    }
    this.location.go(route, query);
  }
}
